import { PermissionsEnum } from '~/constants/Permission';

/**
 * 駅エリア判定に関する業務条件を提供するcomposable。
 *
 * 判定処理の起動可否、駅エリア外警告の表示可否、荷役作業受付の利用可否など、
 * 複数コンポーネントで参照する条件をここに集約する。
 */
export const useStationAreaConditions = () => {
  const { isPc } = useDevice();
  const loginUserStore = useLoginUserStore();
  const { loginUser } = storeToRefs(loginUserStore);
  const { isInsideArea } = storeToRefs(useGeolocationStore());
  const { isLoginPage, isWorkPage } = useRouteConditions();

  /** 荷役作業権限を持っているか */
  const hasCargoEntryPermission = computed(() =>
    loginUserStore.hasPermission(PermissionsEnum.CARGO_ENTRY),
  );

  /** ログインユーザが駅エリア検知対象か */
  const isStationAreaDetectionEnabled = computed(() =>
    loginUser.value?.stationAreaDetectionFlag === true,
  );

  /** 代理ログイン中か */
  const isProxyLogin = computed(() => !!loginUser.value?.proxyLoginMode);

  /** 作業駅が設定されているか */
  const hasWorkingStation = computed(() => !!loginUser.value?.workingStation);

  /**
   * 駅エリア判定が必要なユーザ・端末状態か。
   *
   * 作業駅の有無は「判定を起動できるか」の条件で別途見る。
   * モーダル表示など、作業駅を直接必要としない表示条件でも再利用するため。
   */
  const isStationAreaJudgementRequired = computed(() =>
    !isPc.value
    && isStationAreaDetectionEnabled.value
    && hasCargoEntryPermission.value
    && !isProxyLogin.value,
  );

  /** 駅エリア判定処理を起動してよいか */
  const canRunStationAreaValidation = computed(() =>
    isStationAreaJudgementRequired.value && hasWorkingStation.value,
  );

  /**
   * 駅エリア制限上、荷役作業受付を利用してよいか。
   *
   * 作業駅の設定有無は呼び出し元の画面仕様により判定粒度が異なるため、
   * ここでは駅エリア制限だけを扱う。
   */
  const canUseWorkReceptionByStationArea = computed(() =>
    isPc.value
    || !isStationAreaDetectionEnabled.value
    || isProxyLogin.value
    || isInsideArea.value,
  );

  /** ホーム画面で駅エリア判定ステータスを表示するか */
  const shouldShowStationAreaStatusMessage = computed(() =>
    canRunStationAreaValidation.value && !isInsideArea.value,
  );

  /** 駅エリア外警告モーダルを表示するか */
  const shouldShowOutOfStationAlert = computed(() =>
    isStationAreaJudgementRequired.value
    && !isLoginPage.value
    && !isInsideArea.value
    && isWorkPage.value,
  );

  return {
    hasCargoEntryPermission,
    isStationAreaDetectionEnabled,
    isProxyLogin,
    hasWorkingStation,
    isStationAreaJudgementRequired,
    canRunStationAreaValidation,
    canUseWorkReceptionByStationArea,
    shouldShowStationAreaStatusMessage,
    shouldShowOutOfStationAlert,
  };
};
